import {
  TableRowPlaceholder,
  TdPlaceholder,
} from 'components/superreports/widgets/table/TableRowPlaceholder';
import { ContentFrame } from 'components/ui-blocks/container';
import { Table, Tbody, Th, Thead, Tr } from 'components/ui-blocks/table';
import { useIsListEmpty } from 'hooks/useIsListEmpty';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserPermissions } from 'redux/selectors/user';
import ForecastEmptyPlaceholder from 'routes/forecast/components/ForecastEmptyPlaceholder';
import ForecastTableBatchActions from 'routes/forecast/components/ForecastTableBatchActions';
import useForecastBatchActions from 'routes/forecast/hooks/useForecastBatchActions';
import type { ThunkDispatch } from 'types/redux';
import {
  editForecastId,
  fetchPaginatedForecastsSmartly,
  viewForecastId,
} from '../redux/modules/forecast.actions';
import {
  getForecastLoadingState,
  getForecastSelectionsAsSet,
  getForecastsByAssignee,
} from '../redux/modules/forecast.selectors';
import styles from './ForecastTable.module.scss';
import ForecastAssigneeTr from './table/ForecastAssigneeTr';

interface Props {
  hideProject?: boolean;
}

const ForecastsByAssigneeTable: React.FC<Props> = props => {
  const forecastsByAssignee = useSelector(getForecastsByAssignee);
  const forecastIds = useMemo(
    () => forecastsByAssignee.flatMap(row => row.forecasts.map(f => f.id)),
    [forecastsByAssignee]
  );
  const isLoading = useSelector(getForecastLoadingState);
  const selections = useSelector(getForecastSelectionsAsSet);
  const { isEmpty } = useIsListEmpty(forecastsByAssignee.length, isLoading);
  const batchActionProps = useForecastBatchActions(forecastIds);
  const permissions = useSelector(getUserPermissions);
  const canUpdate = permissions.canUpdate.forecasts;

  const dispatch = useDispatch<ThunkDispatch>();

  const handleOpenRow = useCallback(
    (id: number | null) => {
      dispatch(fetchPaginatedForecastsSmartly({ user_ids: [id] }));
    },
    [dispatch]
  );

  const clickForecast = useCallback(
    (id: number, mode?: 'edit' | 'view') => {
      if (mode === 'edit') {
        dispatch(editForecastId(id, { interaction: 'Click Task' }));
      } else {
        dispatch(viewForecastId(id, { interaction: 'Click Task' }));
      }
    },
    [dispatch]
  );

  function renderBody() {
    if (isLoading && forecastsByAssignee.length === 0) {
      return <TableRowPlaceholder rowCount={20} colCount={8} placeHolder={<TdPlaceholder />} />;
    }
    return forecastsByAssignee.map((row, index) => {
      return (
        <ForecastAssigneeTr
          selections={selections}
          onClickForecast={clickForecast}
          key={index}
          showProject={!props.hideProject}
          forecastIndent={25}
          row={row}
          canUpdate={canUpdate}
          onOpenRow={handleOpenRow}
        />
      );
    });
  }

  if (isEmpty) {
    return <ForecastEmptyPlaceholder isLoading={isLoading} />;
  }

  return (
    <>
      <ForecastTableBatchActions batchActions={batchActionProps} isLoading={isLoading} />
      <ContentFrame spacing>
        <Table className={styles.container} inline rowHover>
          <Thead>
            <Tr>
              <Th colSpan={5}>Person</Th>
              <Th>Estimate</Th>
              <Th>Tasks</Th>
            </Tr>
          </Thead>
          <Tbody>{renderBody()}</Tbody>
        </Table>
      </ContentFrame>
    </>
  );
};

export default ForecastsByAssigneeTable;
